/* eslint-disable */
import {
  AxiosResponse,
} from 'axios';
import EventEmitter from 'eventemitter3';
import {
  ExtendedAxios,
  tryCatch,
} from 'utils';
import {
  ExtendedAxiosRequestConfig,
  ExtendedAxiosResponse,
  ExtendedAxiosError,
} from 'connections';
import {
  Settings,
} from 'settings';

export enum AuthStatus {
  AUTHORIZED,
  AUTHORIZING,
  UNAUTHORIZED,
}
export class BackendAxios extends ExtendedAxios {
  getAccessToken: () => string = () => '';

  getRefreshToken: () => string = () => '';

  setAccessToken: (token: string) => void = () => null;

  setRefreshToken: (token: string) => void = () => null;

  refresh = async (config?: ExtendedAxiosRequestConfig): Promise<ExtendedAxiosResponse> => {
    // Implementation will be provided by the auth store
    return Promise.reject(new Error('refresh method not implemented'));
  };

  logout: () => void = () => null;

  updateTokenHeader = (token: string) => {
    const result = tryCatch(Promise.resolve(this.updateDefaultHeaders({
      Authorization: `Bearer ${token}`,
      'Source-Type': 'admin',
    })));

    result.then(res => {
      if (!res.isSuccess) {
        console.error('Error updating token header:', res.error);
      }
    });
  };
}

export const backendAxiosFactory = () => {
  const MAXIMUM_RETRIES_COUNT = 3;
  const DELAY_MILLISECONDS = 1000;

  const setBaseURL = () => Settings.config.REACT_APP_BASE_URL;
  const baseURL = setBaseURL();

  const backendAxios = new BackendAxios({
    axiosRequestConfig: {
      baseURL,
      timeout: 100000000,
    },
    defaultParams: {},
  });

  let authStatus: AuthStatus = AuthStatus.AUTHORIZED;
  const authEvent = new EventEmitter();

  authEvent.on('change', (value: AuthStatus) => {
    authStatus = value;
  });

  const requestSuccessInterceptor = (async (value: ExtendedAxiosRequestConfig) => {
    // if token is refreshing await the refresh process before dispathing the request
    if (authStatus === AuthStatus.AUTHORIZING) {
      if (value.isRefreshing) {
        return value;
      }

      const authPromise = new Promise<void>((res, rej) => {
        authEvent.on('change', (authValue: AuthStatus) => {
          if (authValue === AuthStatus.AUTHORIZED) {
            res();
          } else if (authValue === AuthStatus.UNAUTHORIZED) {
            rej(new Error('UNAUTHORIZED'));
          }
        });
      });

      const result = await tryCatch(authPromise);
      if (!result.isSuccess) {
        return Promise.reject(result.error);
      }

      value.headers.Authorization = `Bearer ${backendAxios.getAccessToken()}`;
      value.headers['Source-Type'] = 'admin';
    }
    return value;
  });
  backendAxios.instance.interceptors.request.use(requestSuccessInterceptor);

  const fixRemainingRetries = (remainingRetries: number | undefined) => {
    if (remainingRetries !== null && remainingRetries !== undefined) {
      return remainingRetries;
    }
    return MAXIMUM_RETRIES_COUNT - 1;
  };

  const retryRequest = async (config: ExtendedAxiosRequestConfig): Promise<ExtendedAxiosResponse<any>> => {
    const requestPromise = new Promise<AxiosResponse<any>>((res) => {
      setTimeout(() => {
        config.remainingRetries = fixRemainingRetries(config.remainingRetries) - 1;
        if (config.isRefreshing) {
          config.headers.Authorization = `Bearer ${backendAxios.getRefreshToken()}`;
        } else {
          config.headers.Authorization = `Bearer ${backendAxios.getAccessToken()}`;
        }
        res(backendAxios.instance(config));
      }, DELAY_MILLISECONDS);
    });

    const result = await tryCatch(requestPromise);
    if (result.isSuccess) {
      return result.value;
    }
    return Promise.reject(result.error);
  };

  const processFailedRequest = async (error: ExtendedAxiosError): Promise<any> => {
    const remainingRetries = fixRemainingRetries(error.config.remainingRetries);
    if (!remainingRetries) {
      if (error.config.isRefreshing) {
        authEvent.emit('change', AuthStatus.UNAUTHORIZED);
        backendAxios.logout();
      }
      return Promise.reject(error);
    }

    // if response status is UNAUTHORIZED
    if (error.response && error.response.status === 401) {
      // if refresh access token process is in progress
      if (authStatus === AuthStatus.AUTHORIZING) {
        if (error.config.isRefreshing) {
          // if this is the refresh access token request
          // logout and return promise rejection
          authEvent.emit('change', AuthStatus.UNAUTHORIZED);
          backendAxios.logout();
          return Promise.reject(error);
        }

        // if this is any other request
        // wait until refresh has ended
        const authPromise = new Promise<void>((res, rej) => {
          authEvent.on('change', (authValue: AuthStatus) => {
            if (authValue === AuthStatus.AUTHORIZED) {
              res();
            } else if (authValue === AuthStatus.UNAUTHORIZED) {
              rej(new Error('UNAUTHORIZED'));
            }
          });
        });

        const authResult = await tryCatch(authPromise);
        if (!authResult.isSuccess) {
          return Promise.reject(authResult.error);
        }

        // then retry request
        const retryResult = await tryCatch<ExtendedAxiosResponse, ExtendedAxiosError>(retryRequest(error.config));
        if (retryResult.isSuccess) {
          return retryResult.value;
        }

        return processFailedRequest(retryResult.error);

      } else if (authStatus === AuthStatus.AUTHORIZED) {
        // if access token expired or is not working anymore
        // change authstatus to authorizing
        authEvent.emit('change', AuthStatus.AUTHORIZING);
        const config: ExtendedAxiosRequestConfig = {
          isRefreshing: true,
          originalRequest: error.config,
        };

        const refreshResult = await tryCatch<ExtendedAxiosResponse, ExtendedAxiosError>(backendAxios.refresh(config));
        if (!refreshResult.isSuccess) {
          authEvent.emit('change', AuthStatus.UNAUTHORIZED);
          return processFailedRequest(refreshResult.error);
        }

        authEvent.emit('change', AuthStatus.AUTHORIZED);

        const originalRequestConfig = {
          ...error.config,
          headers: {
            Authorization: `Bearer ${backendAxios.getAccessToken()}`,
          },
        } as any;

        const originalResult = await tryCatch<ExtendedAxiosResponse, ExtendedAxiosError>(retryRequest(originalRequestConfig));
        if (originalResult.isSuccess) {
          return originalResult.value;
        }

        return processFailedRequest(originalResult.error);
      }
    } else {
      const retryResult = await tryCatch<ExtendedAxiosResponse, ExtendedAxiosError>(retryRequest(error.config));
      if (retryResult.isSuccess) {
        return retryResult.value;
      }

      return processFailedRequest(retryResult.error);
    }

    // Fallback in case no conditions are met
    return Promise.reject(error);
  };

  const responseSuccessInterceptor = (value: AxiosResponse<any>) => value;
  const responseFailureInterceptor = async (error: ExtendedAxiosError) => {
    const result = await tryCatch(processFailedRequest(error));
    if (result.isSuccess) {
      return result.value;
    }
    return Promise.reject(result.error);
  };
  backendAxios.instance.interceptors.response.use(responseSuccessInterceptor, responseFailureInterceptor);
  /* eslint-enable @typescript-eslint/no-explicit-any */
  return backendAxios;
};
